import { useState, useRef, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { DefaultBuildingImage } from "./DefaultBuildingImage";
import { useImagePerformance } from "@/hooks/useImagePerformance";

interface OptimizedArticleImageProps {
  src?: string;
  alt: string;
  className?: string;
  priority?: boolean; // For above-the-fold images
  aspectRatio?: 'square' | 'video' | 'wide'; // Common aspect ratios
}

export const OptimizedArticleImage = ({
  src,
  alt,
  className = "",
  priority = false,
  aspectRatio = 'video'
}: OptimizedArticleImageProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority); // Load immediately if priority
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { trackImageStart, trackImageLoad } = useImagePerformance();

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !containerRef.current || !src) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          trackImageStart(src);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px' // Start loading 50px before entering viewport
      }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [priority, src, trackImageStart]);

  // Track performance for priority images
  useEffect(() => {
    if (priority && src) {
      trackImageStart(src);
    }
  }, [priority, src, trackImageStart]);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
    if (src) {
      trackImageLoad(src, true);
    }
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
    if (src) {
      trackImageLoad(src, false);
    }
  };

  // Aspect ratio classes
  const aspectClasses = {
    square: 'aspect-square',
    video: 'aspect-video', 
    wide: 'aspect-[21/9]'
  };

  const shouldShowImage = src && isInView;
  const showFallback = !src || hasError;

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden rounded-lg ${aspectClasses[aspectRatio]} ${className}`}
    >
      {/* Loading skeleton */}
      {isLoading && shouldShowImage && (
        <Skeleton className="absolute inset-0 w-full h-full" />
      )}

      {/* Actual image */}
      {shouldShowImage && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoading ? 'opacity-0' : 'opacity-100'
          }`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          // Preload hint for critical images
          fetchPriority={priority ? "high" : "auto"}
        />
      )}

      {/* Fallback to default building image */}
      {showFallback && !isLoading && (
        <DefaultBuildingImage className="w-full h-full" />
      )}

      {/* Placeholder for images not yet in view */}
      {!isInView && !priority && (
        <div className="w-full h-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
          <div className="w-8 h-8 bg-muted-foreground/20 rounded-full animate-pulse" />
        </div>
      )}
    </div>
  );
};